import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ExclamationCircleIcon,
  PlayIcon
} from '@heroicons/react/24/outline';
import { useVideoGallery } from '../../contexts/VideoGalleryContext';

interface VideoFormData {
  youtube_id: string;
  title: string;
  description: string;
  is_short: boolean;
  display_order: number;
  is_published: boolean;
}

// YouTube URL에서 ID 추출
const extractYoutubeId = (input: string): string => {
  const trimmed = input.trim();

  // youtube.com/shorts/ID
  const shortsMatch = trimmed.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/);
  if (shortsMatch) return shortsMatch[1];

  // youtube.com/watch?v=ID
  const watchMatch = trimmed.match(/[?&]v=([a-zA-Z0-9_-]{11})/);
  if (watchMatch) return watchMatch[1];

  // youtu.be/ID
  const shortUrlMatch = trimmed.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/);
  if (shortUrlMatch) return shortUrlMatch[1];

  // youtube.com/embed/ID
  const embedMatch = trimmed.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/);
  if (embedMatch) return embedMatch[1];

  // 11자리 ID 직접 입력
  if (/^[a-zA-Z0-9_-]{11}$/.test(trimmed)) return trimmed;

  return trimmed;
};

const VideoGalleryForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { addVideo, updateVideo, getVideo, videos } = useVideoGallery();
  const isEditMode = !!id;

  const [formData, setFormData] = useState<VideoFormData>({
    youtube_id: '',
    title: '',
    description: '',
    is_short: false,
    display_order: videos.length + 1,
    is_published: true
  });

  const [youtubeInput, setYoutubeInput] = useState('');
  const [errors, setErrors] = useState<Partial<Record<keyof VideoFormData, string>>>({});

  // Edit mode: Load existing data
  useEffect(() => {
    if (isEditMode && id) {
      const video = getVideo(Number(id));
      if (video) {
        setFormData({
          youtube_id: video.youtube_id,
          title: video.title,
          description: video.description || '',
          is_short: video.is_short,
          display_order: video.display_order,
          is_published: video.is_published
        });
        setYoutubeInput(video.youtube_id);
      }
    }
  }, [isEditMode, id, getVideo]);

  const handleYoutubeInputChange = (value: string) => {
    setYoutubeInput(value);
    const extracted = extractYoutubeId(value);
    setFormData(prev => ({ ...prev, youtube_id: extracted }));
    if (errors.youtube_id) {
      setErrors(prev => ({ ...prev, youtube_id: '' }));
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name as keyof VideoFormData]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Partial<Record<keyof VideoFormData, string>> = {};

    if (!formData.youtube_id.trim()) {
      newErrors.youtube_id = 'YouTube 영상 ID 또는 URL은 필수입니다.';
    } else if (!/^[a-zA-Z0-9_-]{11}$/.test(formData.youtube_id)) {
      newErrors.youtube_id = '유효한 YouTube 영상 ID가 아닙니다. URL 또는 11자리 ID를 입력해주세요.';
    }

    if (!formData.title.trim()) {
      newErrors.title = '제목은 필수입니다.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      try {
        const videoData = {
          youtube_id: formData.youtube_id,
          title: formData.title,
          description: formData.description || undefined,
          is_short: formData.is_short,
          display_order: Number(formData.display_order),
          is_published: formData.is_published
        };

        if (isEditMode && id) {
          await updateVideo(Number(id), videoData);
          alert('영상이 성공적으로 수정되었습니다!');
        } else {
          await addVideo(videoData as any);
          alert('영상이 성공적으로 등록되었습니다!');
        }
        navigate('/admin/video-gallery');
      } catch (error) {
        console.error('Failed to submit video:', error);
        alert('영상 저장에 실패했습니다.');
      }
    }
  };

  const isValidYoutubeId = /^[a-zA-Z0-9_-]{11}$/.test(formData.youtube_id);

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          {isEditMode ? '영상 수정' : '영상 등록'}
        </h1>
        <p className="text-gray-600">
          {isEditMode ? '영상 정보를 수정합니다.' : '새로운 YouTube 영상을 등록합니다.'}
          <span className="text-red-500">*</span> 표시는 필수 항목입니다.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-8">
        {/* YouTube 영상 */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">YouTube 영상</h2>

          <div className="space-y-6">
            {/* YouTube URL/ID 입력 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                YouTube URL 또는 영상 ID <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={youtubeInput}
                onChange={(e) => handleYoutubeInputChange(e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${
                  errors.youtube_id ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="https://www.youtube.com/watch?v=... 또는 영상 ID"
              />
              {formData.youtube_id && (
                <p className="mt-1 text-xs text-gray-500">
                  추출된 ID: <span className="font-mono bg-gray-100 px-1 rounded">{formData.youtube_id}</span>
                </p>
              )}
              {errors.youtube_id && (
                <p className="mt-1 text-sm text-red-500 flex items-center">
                  <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                  {errors.youtube_id}
                </p>
              )}
            </div>

            {/* 미리보기 */}
            {isValidYoutubeId && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  미리보기
                </label>
                <div className={`relative rounded-xl overflow-hidden bg-gray-900 ${
                  formData.is_short ? 'aspect-[9/16] max-w-[200px]' : 'aspect-video max-w-[480px]'
                }`}>
                  <img
                    src={`https://img.youtube.com/vi/${formData.youtube_id}/hqdefault.jpg`}
                    alt="미리보기"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <a
                      href={`https://www.youtube.com/watch?v=${formData.youtube_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-14 h-14 bg-black/60 rounded-full flex items-center justify-center hover:bg-black/80 transition-colors"
                    >
                      <PlayIcon className="w-7 h-7 text-white ml-0.5" />
                    </a>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 기본 정보 */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">기본 정보</h2>

          <div className="space-y-6">
            {/* 제목 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                제목 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${
                  errors.title ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="영상 제목을 입력하세요"
              />
              {errors.title && (
                <p className="mt-1 text-sm text-red-500 flex items-center">
                  <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                  {errors.title}
                </p>
              )}
            </div>

            {/* 설명 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                설명
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="영상에 대한 설명을 입력하세요 (선택사항)"
              />
            </div>

            {/* 표시 순서 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                표시 순서
              </label>
              <input
                type="number"
                name="display_order"
                value={formData.display_order}
                onChange={handleInputChange}
                min={1}
                className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              />
              <p className="mt-1 text-xs text-gray-500">숫자가 작을수록 먼저 표시됩니다.</p>
            </div>

            {/* 체크박스들 */}
            <div className="space-y-3">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_short}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_short: e.target.checked }))}
                  className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                />
                <span className="text-sm font-medium text-gray-700">YouTube Shorts 영상</span>
              </label>
              <p className="ml-6 text-xs text-gray-500">체크하면 세로(9:16) 비율로 표시됩니다.</p>

              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_published}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_published: e.target.checked }))}
                  className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                />
                <span className="text-sm font-medium text-gray-700">공개</span>
              </label>
              <p className="ml-6 text-xs text-gray-500">체크 해제하면 홈페이지에 표시되지 않습니다.</p>
            </div>
          </div>
        </div>

        {/* 버튼 */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/admin/video-gallery')}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            취소
          </button>
          <button
            type="submit"
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            {isEditMode ? '수정하기' : '등록하기'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default VideoGalleryForm;
